import { useClickOutside } from '@mnd-frontend/hooks';
import {
  Badge,
  GREY_300,
  GREY_500,
  GREY_900,
  Icons,
  NEUTRAL_WHITE,
  RED_200,
  RED_700,
  SPACING_1,
  SPACING_1_5,
  SPACING_2,
  SPACING_3,
  SPACING_8,
  Text,
} from '@mnd-frontend/ui';
import { useRef, useState } from 'react';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';
import { track } from '../../../../../lib/tracker';
import NavigationLink from '../../Header/NavigationLink';
import { type MainMenuItem, MenuState } from '../types';

const NavItem = styled.li<{ $open: boolean; $active: boolean }>`
  width: 100%;
  background: ${({ $active }) => ($active ? RED_200 : 'transparent')};
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${({ $active }) => !$active && `background-color: ${GREY_300};`}
    }
  }
  > a {
    font-weight: 500;
    color: ${({ $active }) => ($active ? RED_700 : GREY_900)};
    padding: ${SPACING_1_5} ${SPACING_2};
    display: flex;
    gap: ${SPACING_1};
  }
  @media (hover: hover) and (pointer: fine) {
    background-color: ${({ $active }) => ($active ? RED_200 : 'GREY_200')};
  }
`;
const NavText = styled.div`
  flex: 1;
`;
const NavigationButton = styled.button<{ $active: boolean }>`
  border: none;
  width: 100%;
  background-color: transparent;
  color: ${({ $active }) => ($active ? RED_700 : GREY_900)};
  display: flex;
  flex-direction: row;
  gap: ${SPACING_1};
  padding: ${SPACING_1_5} ${SPACING_2};
  align-items: center;
  & i:last-child {
    margin-left: auto;
  }
  ${({ $active }) => $active && `border-bottom: 2px solid ${NEUTRAL_WHITE};`}
`;

const SubNav = styled.ul<{ $open: boolean }>`
  background: ${NEUTRAL_WHITE};
  display: ${({ $open }) => ($open ? 'grid' : 'none')};
  list-style: none;
  padding: 0;
  grid-auto-rows: 44px;
`;

const SubNavItem = styled.li<{ $active: boolean }>`
  display: flex;
  align-items: center;
  padding-left: ${SPACING_8};
  width: 100%;
  background-color: ${({ $active }) => ($active ? RED_200 : NEUTRAL_WHITE)};
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${({ $active }) => !$active && `background-color: ${GREY_300};`}
    }
  }
  & a {
    color: ${({ $active }) => ($active ? RED_700 : GREY_900)};
    width: 100%;
    display: flex;
    align-items: center;
    gap: ${SPACING_1};
    padding-right: 20px;
    > *:not(${NavText}) {
      flex-shrink: 0;
    }
  }
`;

const Divider = styled.div`
  height: 1px;
  border: 1px solid ${GREY_500};
  border-width: 1px 0 0 0;
  margin-top: ${SPACING_3};
  margin-bottom: ${SPACING_3};
`;

interface MobileNavItemProps {
  itemData: MainMenuItem;
  topGroup: MenuState[];
  close: () => void;
}

const MobileNavItem = ({ itemData, topGroup, close }: MobileNavItemProps) => {
  const location = useLocation();
  const [subNavOpen, setSubNavOpen] = useState<MenuState | null>(null);
  const pressroomListRef = useRef<HTMLDivElement>(null);
  const renderAsLink = itemData.items && itemData.items.length === 1;
  const active = !topGroup.includes(itemData.menuState)
    ? false
    : itemData.matchingRoutes
      ? !!itemData.matchingRoutes.find(url => matchPath(url, location.pathname))
      : false;
  const iconToRender = active ? itemData.iconActive : itemData.icon;
  const arialLabelledBy = `mobile-nav-item-button-${itemData.menuState}`;
  const ariaControls = `mobile-sub-nav-${itemData.menuState}`;

  const handleOnClick = () => {
    setSubNavOpen(subNavOpen === itemData.menuState ? null : itemData.menuState);
  };

  useClickOutside(pressroomListRef, () => {
    if (subNavOpen === MenuState.PressroomSelector) {
      setSubNavOpen(null);
    }
  });

  return (
    <>
      <NavItem key={itemData.menuState} $open={!!subNavOpen} $active={active}>
        {renderAsLink ? (
          <NavigationLink
            href={itemData.items[0].url}
            onClick={() => {
              close();
              track({
                event: 'Navigation link: clicked',
                traits: { menuCategory: itemData.menuState, linkText: itemData.items[0].text },
              });
            }}
          >
            {iconToRender}
            <Text as="span" variant="labelMedium">
              {itemData.heading}
            </Text>
          </NavigationLink>
        ) : (
          <>
            <NavigationButton
              type="button"
              onClick={handleOnClick}
              id={arialLabelledBy}
              aria-expanded={!!subNavOpen}
              aria-controls={ariaControls}
              $active={active}
            >
              {iconToRender}
              <Text as="span" variant="labelMedium">
                {itemData.heading}
              </Text>
              {subNavOpen === itemData.menuState ? <Icons.ChevronUp /> : <Icons.ChevronDown />}
            </NavigationButton>
            <SubNav
              $open={!!subNavOpen}
              id={ariaControls}
              aria-labelledby={arialLabelledBy}
              hidden={subNavOpen !== itemData.menuState}
            >
              {itemData.items.map(item => {
                const isPublications = item.text === 'Publications';
                const active = !topGroup.includes(itemData.menuState)
                  ? false
                  : isPublications
                    ? Array.isArray(item.activeUrl)
                      ? !!item.activeUrl.find(url => matchPath(url, location.pathname))
                      : !!matchPath(item.activeUrl ?? '', location.pathname)
                    : item.url === location.pathname;
                return (
                  <SubNavItem key={item.url} $active={active}>
                    <NavigationLink
                      href={item.url}
                      onClick={() => {
                        close();
                        track({
                          event: 'Navigation link: clicked',
                          traits: { menuCategory: itemData.menuState, linkText: item.text },
                        });
                      }}
                    >
                      <NavText>{item.text}</NavText>
                      {item.badge && <Badge variant={item.badge.variant} text={item.badge.text} />}
                      {item.newTab && <Icons.ExternalLink $size="xxs" />}
                    </NavigationLink>
                  </SubNavItem>
                );
              })}
            </SubNav>
          </>
        )}
      </NavItem>
      {itemData.menuState === MenuState.Reports && <Divider aria-hidden="true" />}
    </>
  );
};

export default MobileNavItem;
